import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { keyframes } from "@emotion/react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import palette from "../../common/styles/palette";
import { renderTextoComNegrito } from "./MensagemArquivos";
import {
  InterviewAmendment,
  InterviewEnrichment,
  InterviewEnrichmentEngine,
  InterviewOp,
} from "../../types/Interview";

/**
 * A revisão do Aprofundamento: as Emendas que a IA propôs, o conteúdo atual e
 * as alterações que entrarão no Rascunho quando o professor as adotar.
 *
 * POR QUE ESTA TELA EXISTE, e por que ela não podia ser um botão que aplica.
 * O professor responde curto — «Encontra um paciente sentado no chao com a mao
 * no peito» — e o Rascunho fica pobre. A camada que conserta isso precisa
 * SOBRESCREVER o que ele escreveu, e sobrescrever é exatamente o que a geração
 * é proibida de fazer, porque não existe desfazer: `revision` é contador de
 * concorrência, não histórico de conteúdo. Uma proposta que ele lê campo a
 * campo É o desfazer que faltava — o que ele não marcar nunca tocou o Rascunho.
 *
 * O COMPARATIVO DE DOIS MOTORES, e o que ele exige a mais:
 *
 *  - CADA MOTOR TEM REGIÃO PRÓPRIA. Gemini e MedGemma escrevem pareceres
 *    autônomos sobre o MESMO Rascunho; misturá-los numa lista só faria o
 *    professor decidir sem saber quem propôs o quê.
 *  - COR NUNCA É A ÚNICA PROVENIÊNCIA. Azul e verde ajudam a varrer a tela,
 *    mas cada cartão repete o nome do modelo em texto: daltonismo, impressão e
 *    captura de tela não podem apagar a autoria.
 *  - ALTERNATIVA NÃO É ETAPA. Quando os dois reescrevem o mesmo campo, marcar
 *    um desmarca o outro — o servidor recusa o lote contraditório, e deixar o
 *    professor marcar dez cartões para só então recusar seria uma armadilha.
 *  - CONHECIMENTO MÉDICO DE MODELO É PISTA, NÃO FONTE. A fundamentação vem
 *    rotulada como não verificada, sem selo de conferido.
 *
 * TRÊS DECISÕES DE DESENHO QUE VÊM DE ANTES E CONTINUAM VALENDO:
 *
 *  - NADA VEM MARCADO. Aceitar por omissão transformaria a revisão em teatro.
 *  - O «POR QUÊ» FICA VISÍVEL, NÃO EM TOOLTIP. É o critério de decisão.
 *  - O ANTES VEM DO SERVIDOR. Ele é lido do Rascunho, nunca escrito pelo
 *    modelo. Um diff narrado por quem propõe a mudança não é um diff.
 *
 * O COMPONENTE É BURRO. Não chama service, não conhece `interview_id`, não
 * guarda Rascunho. Recebe a proposta e devolve os ids marcados.
 */

/** As gavetas, na ordem em que o professor pensa a Estação. */
const ORDEM: InterviewAmendment["category"][] = [
  "cena",
  "paciente",
  "ator",
  "ambiente",
  "checklist",
  "deteccao",
  "outros",
];

const ROTULO_CATEGORIA: Record<InterviewAmendment["category"], string> = {
  cena: "interview_enrich_cat_cena",
  paciente: "interview_enrich_cat_paciente",
  ator: "interview_enrich_cat_ator",
  ambiente: "interview_enrich_cat_ambiente",
  checklist: "interview_enrich_cat_checklist",
  deteccao: "interview_enrich_cat_deteccao",
  outros: "interview_enrich_cat_outros",
};

const ROTULO_CAMINHO: Record<string, string> = {
  name: "Nome do Cenário",
  instructions: "Instrução de avaliação",
  "scenario.goal_scene": "Objetivo da Cena",
  "scenario.description_scene": "Descrição da Cena",
  "scenario.scenario_evaluated": "Enunciado para o Avaliado",
  "scenario.actor": "Tipo de ator",
  "scenario.actor_instructions": "Roteiro do Ator",
  "scenario.materials": "Materiais",
  "scenario.patient_disclosure.spontaneous": "Falas espontâneas do paciente",
  "scenario.patient_disclosure.on_demand": "Falas sob demanda do paciente",
};

const ROTULO_FACETA: Record<string, string> = {
  speech: "Fala esperada",
  video: "Ação visual",
  correct_when: "Aprovar quando",
  not_done_when: "Não realizou quando",
  note: "Tolerâncias e exceções",
};

/**
 * A identidade visual de cada motor.
 *
 * `titulo` NÃO existe aqui de propósito: o nome do motor na tela é o campo
 * `model` que voltou da rodada (ex.: `gemini-3.8-flash`) — uma constante fixa
 * «Gemini 3.8 Flash» no código arriscaria divergir do chip com o nome real
 * retornado pelo servidor. Dois nomes para a mesma coluna é pior que nenhum.
 *
 * `emeraldDeep` sobre `emeraldSoft` mede 3,22:1 — insuficiente para texto
 * normal. O verde do texto vem de `#04785d`, medido em 5,8:1 sobre o mesmo
 * fundo; a cor viva fica só na borda e no ícone, onde contraste de texto não
 * se aplica.
 */
const IDENTIDADE_MOTOR: Record<
  string,
  { papel: string; cor: string; texto: string; fundo: string }
> = {
  gemini: {
    papel: "interview_enrich_role_general",
    cor: "#1e6fe0",
    texto: palette.info,
    fundo: palette.infoSoft,
  },
  medgemma: {
    papel: "interview_enrich_role_medical",
    cor: palette.emeraldDeep,
    texto: "#04785d",
    fundo: palette.emeraldSoft,
  },
  legacy_unknown: {
    papel: "interview_enrich_role_unknown",
    cor: palette.textMuted,
    texto: palette.textMuted,
    fundo: palette.muted,
  },
};

const identidadeDe = (engine: string) =>
  IDENTIDADE_MOTOR[engine] ?? IDENTIDADE_MOTOR.legacy_unknown;

/**
 * O output do MedGemma ganha identidade própria: verde + um brilho pulsante na
 * entrada, para o professor distinguir de cara o que veio do modelo médico do
 * que ele mesmo escreveu.
 */
const brilhoMedgemma = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(0, 153, 102, 0.40); }
  60%  { box-shadow: 0 0 0 10px rgba(0, 153, 102, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 153, 102, 0); }
`;

const surgirMedgemma = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const iconeDe = (engine: string) => {
  if (engine === "gemini") return <BoltOutlinedIcon fontSize="small" />;
  if (engine === "medgemma") return <LocalHospitalOutlinedIcon fontSize="small" />;
  return <HelpOutlineOutlinedIcon fontSize="small" />;
};

const valorLegivel = (valor: string | number | null | string[]) => {
  if (valor === null || valor === "") return "vazio";
  if (Array.isArray(valor)) return valor.join("; ");
  return String(valor);
};

/** Resposta bruta do modelo, reindentada quando é JSON; senão, o texto como veio. */
const rawLegivel = (raw: string): string => {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

/**
 * Emenda composta é atômica por causa das referências entre as operações.
 * Portanto o cartão precisa declarar cada efeito que o checkbox autoriza.
 */
const descreverOp = (op: InterviewOp): string => {
  switch (op.op) {
    case "set":
      return `${op.path === "checklist.camera2_guidance" ? "Enquadramento da câmera focal" : ROTULO_CAMINHO[op.path] ?? op.path}: ${valorLegivel(op.value)}`;
    case "add_skill":
      return `Criar seção “${op.name}” com ${op.points} pontos`;
    case "add_item":
      return `Criar item “${op.question}” na seção ${op.skill_ref}, com ${op.value} pontos`;
    case "remove_item":
      return `Remover o item ${op.ref}`;
    case "remove_skill":
      return `Remover a seção ${op.ref}`;
    case "set_detection":
      return `${ROTULO_FACETA[op.field] ?? op.field}: ${op.text}`;
    case "set_detection_camera2":
      return `Câmera focal — ${ROTULO_FACETA[op.field] ?? op.field}: ${op.text}`;
    case "set_critical":
      return `${op.value ? "Marcar" : "Desmarcar"} o item ${
        op.item_ref
      } como eliminatório`;
    // As Etapas obrigatórias (ADR-0003). Descreve a CONTAGEM e a ordem, não a
    // lista: um cartão de Emenda com doze etapas dentro deixa de ser
    // revisável, que é justamente o que o teto de Emendas evita.
    case "set_steps":
    case "set_steps_camera2": {
      const camera = op.op === "set_steps_camera2" ? "Câmera focal — " : "";
      if (op.steps.length === 0) {
        return `${camera}Remover as etapas obrigatórias do item ${op.item_ref}`;
      }
      const ordenada = op.steps.some((p) => typeof p.order === "number");
      return `${camera}${op.steps.length} etapa(s) obrigatória(s) do item ${op.item_ref}${
        ordenada ? ", em ordem exigida" : ""
      }: ${op.steps.map((p) => p.text).join(" → ")}`;
    }
  }
};

const estrutural = (a: InterviewAmendment) =>
  a.ops.some((op) => op.op === "add_item" || op.op === "add_skill");

/** Lista do servidor, tolerante ao que o PHP serializa como `[]` ou omite. */
const lista = <T,>(valor: T[] | undefined | null): T[] =>
  Array.isArray(valor) ? valor : [];

const texto = (valor: string | undefined | null): string =>
  typeof valor === "string" ? valor : "";

/**
 * A Emenda traz fundamentação clínica de verdade?
 *
 * MEDIDO no app real: o PHP serializa um array associativo VAZIO como `[]`, e
 * `[]` é truthy em JavaScript. Um simples `emenda.medical && …` deixava passar
 * o array vazio, e o primeiro `medical.guidelines.length` derrubava a árvore
 * inteira do React — a tela ficava em branco, sem nenhuma pista na interface.
 * Por isso a checagem é por CONTEÚDO, nunca pela existência do campo.
 */
const temFundamentacao = (
  medical: InterviewAmendment["medical"]
): boolean => {
  if (!medical || Array.isArray(medical)) return false;

  return (
    texto(medical.clinical_rationale).trim() !== "" ||
    texto(medical.pathophysiology).trim() !== "" ||
    lista(medical.guidelines).length > 0 ||
    lista(medical.ontologies).length > 0 ||
    lista(medical.safety_flags).length > 0
  );
};

interface Props {
  aberto: boolean;
  /** Presente quando o pedido é só para redigir as regras de um Item com Gemini. */
  itemEmRevisao?: string | null;
  cameraFocal?: boolean;
  enquadramentoFocal?: boolean;
  /** `null` enquanto nunca se pediu nada nesta Entrevista. */
  proposta: InterviewEnrichment | null;
  /** true durante a varredura, que leva até dois minutos. */
  carregando: boolean;
  /** true enquanto a adoção está em voo. */
  adotando: boolean;
  /**
   * O que está sendo enviado ao modelo neste instante — contagem de seções e
   * itens do Rascunho em edição, para o professor ver a chamada, não só o
   * resultado. `null` quando não há Rascunho para enviar.
   */
  resumoPayload?: { sections: number; items: number; chars: number } | null;
  aoFechar: () => void;
  /** Repete a revisão com a ênfase escolhida no painel. */
  aoPedir: (steer: string) => void;
  aoAdotar: (ids: string[]) => void;
  aoDescartar: () => void;
  erro?: string | null;
  sincronizando?: boolean;
}

export default function PainelAprofundar({
  aberto,
  itemEmRevisao = null,
  cameraFocal = false,
  enquadramentoFocal = false,
  proposta,
  carregando,
  sincronizando = false,
  adotando,
  resumoPayload,
  aoFechar,
  aoPedir,
  aoAdotar,
  aoDescartar,
  erro,
}: Props) {
  const { t } = useTranslation();

  const [marcados, setMarcados] = useState<Record<string, boolean>>({});
  const [steer, setSteer] = useState("");
  const [solicitado, setSolicitado] = useState(false);

  useEffect(() => {
    if (!aberto) setSolicitado(false);
  }, [aberto]);

  useEffect(() => {
    if (aberto && (carregando || sincronizando)) setSolicitado(true);
  }, [aberto, carregando, sincronizando]);

  // `proposal_id` é a identidade da proposta. `created_at` só tem precisão de
  // segundos e pode se repetir quando uma nova varredura substitui a anterior.
  useEffect(() => {
    setMarcados({});
  }, [proposta?.proposal_id]);

  const amendments = useMemo(
    () => proposta?.amendments ?? [],
    [proposta?.amendments]
  );
  const emEspera = carregando || sincronizando;


  /**
   * Um bloco por motor, na ordem em que os motores voltaram do servidor.
   *
   * Propostas gravadas antes do comparativo não têm `engines`; nesse caso o
   * bloco é derivado das próprias Emendas, para que a tela antiga continue
   * funcionando sem inventar um motor que ninguém observou.
   */
  const blocos = useMemo(() => {
    const declarados: InterviewEnrichmentEngine[] = proposta?.engines?.length
      ? proposta.engines
      : Array.from(new Set(amendments.map((a) => a.engine))).map((engine) => ({
          engine,
          model: amendments.find((a) => a.engine === engine)?.model ?? engine,
          role: "reviewer",
          status: "completed" as const,
          summary: "",
          amendment_count: amendments.filter((a) => a.engine === engine).length,
          message: null,
        }));

    // Motor `skipped` não foi consultado: não ganha coluna. É o que faz a
    // revisão médica isolada aparecer como UMA leitura, não como um
    // comparativo com metade vazia.
    return declarados
      .filter((motor) => motor.status !== "skipped")
      .map((motor) => ({
        motor,
        grupos: ORDEM.map((categoria) => ({
          categoria,
          itens: amendments.filter(
            (a) => a.engine === motor.engine && a.category === categoria
          ),
        })).filter((g) => g.itens.length > 0),
      }));
  }, [proposta?.engines, amendments]);

  // A ordem da adoção é a da PROPOSTA, não a do clique: é assim que o servidor
  // aplica, e mostrar outra ordem aqui mentiria sobre o resultado.
  const escolhidos = amendments.filter((a) => marcados[a.id]).map((a) => a.id);

  const contagemPorMotor = useMemo(() => {
    const contagem: Record<string, number> = {};
    amendments
      .filter((a) => marcados[a.id])
      .forEach((a) => {
        contagem[a.model] = (contagem[a.model] ?? 0) + 1;
      });
    return contagem;
  }, [amendments, marcados]);

  const nEstruturais = amendments.filter(estrutural).length;
  const temAlternativas = amendments.some((a) => (a.conflicts_with ?? []).length > 0);

  /**
   * Marcar uma Emenda desmarca as incompatíveis com ela.
   *
   * Duas regras de exclusão, e elas são diferentes:
   *  - INCLUSÃO ESTRUTURAL: cada uma exibe uma redistribuição de pontos
   *    calculada para ela sozinha. Duas no mesmo lote teriam uma terceira
   *    distribuição, que o professor não revisou.
   *  - ALTERNATIVA (`conflicts_with`): dois motores reescrevendo o mesmo campo.
   *    Não são etapas; adotar as duas deixaria a ordem interna decidir.
   */
  const alternar = (a: InterviewAmendment) =>
    setMarcados((atuais) => {
      if (atuais[a.id]) return { ...atuais, [a.id]: false };

      const proximos = { ...atuais };

      (a.conflicts_with ?? []).forEach((rival) => {
        proximos[rival] = false;
      });

      if (estrutural(a)) {
        amendments.filter(estrutural).forEach((item) => {
          proximos[item.id] = false;
        });
      }

      proximos[a.id] = true;
      return proximos;
    });

  /**
   * O lote seguro: tudo o que não obriga a escolher entre duas versões.
   *
   * Uma Emenda com alternativa NUNCA entra aqui — marcar a primeira da lista
   * seria eleger um motor pela ordem do array, que é decisão do professor.
   */
  const selecionarSemAlternativas = () => {
    let incluiuEstrutural = false;

    setMarcados(
      Object.fromEntries(
        amendments.map((a) => {
          if ((a.conflicts_with ?? []).length > 0) return [a.id, false];
          if (!estrutural(a)) return [a.id, true];
          if (incluiuEstrutural) return [a.id, false];
          incluiuEstrutural = true;
          return [a.id, true];
        })
      )
    );
  };

  // Fechar durante a varredura é permitido: ela dura até dois minutos e a
  // proposta é persistida no servidor. Só a gravação da adoção prende a tela.
  const podeFechar = !adotando;

  const enginesPresentes = useMemo(() => {
    if (proposta?.engines?.length) {
      return Array.from(
        new Set(
          proposta.engines
            .filter((e) => e.status !== "skipped")
            .map((e) => e.engine)
        )
      );
    }
    return Array.from(new Set(amendments.map((a) => a.engine)));
  }, [proposta, amendments]);

  const soMedgemma = enginesPresentes.length === 1 && enginesPresentes[0] === "medgemma";
  const soGemini = enginesPresentes.length === 1 && enginesPresentes[0] === "gemini";
  const regrasDoItem = itemEmRevisao !== null;
  return (
    <Drawer
      anchor="right"
      open={aberto}
      onClose={podeFechar ? aoFechar : undefined}
      PaperProps={{
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": "aprofundar-titulo",
        "aria-describedby": "aprofundar-intro",
        sx: {
          width: {
            xs: "100%",
            md: "min(760px, 100vw)",
            lg: "min(1040px, calc(100vw - 32px))",
          },
          maxWidth: "100%",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5, flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {regrasDoItem || soGemini ? (
              <BoltOutlinedIcon
                sx={{ fontSize: 20, color: IDENTIDADE_MOTOR.gemini.texto }}
              />
            ) : soMedgemma ? (
              <LocalHospitalOutlinedIcon
                sx={{ fontSize: 20, color: IDENTIDADE_MOTOR.medgemma.texto }}
              />
            ) : (
              <AutoAwesomeOutlinedIcon
                sx={{ fontSize: 20, color: palette.brand }}
              />
            )}
            <Typography
              id="aprofundar-titulo"
              component="h2"
              sx={{
                flex: 1,
                fontWeight: 700,
                fontSize: 16,
                color: palette.text,
              }}
            >
              {enquadramentoFocal ? "Enquadramento da câmera focal para todo o checklist" : regrasDoItem
                ? `Regras para análise do vídeo: ${itemEmRevisao}`
                : soGemini
                ? "Aprofundamento pedagógico (Gemini)"
                : soMedgemma
                ? t("interview_enrich_title")
                : "Aprofundamento comparativo (Gemini + MedGemma)"}
            </Typography>
            <Button
              size="small"
              disabled={!podeFechar}
              onClick={aoFechar}
              startIcon={<CloseOutlinedIcon fontSize="small" />}
              sx={{ minWidth: "auto", textTransform: "none" }}
            >
              {t("btn_close")}
            </Button>
          </Box>
          <Typography
            id="aprofundar-intro"
            sx={{ mt: 0.75, fontSize: 14, color: palette.textMuted }}
          >
            {enquadramentoFocal ? "O Gemini propõe uma única recomendação de posição, ângulo e enquadramento. Confira e adote a sugestão para atualizar este campo." : cameraFocal
              ? "O Gemini propõe evidências visuais complementares conforme o cenário e o critério deste item. Confira as regras e marque as que deseja adotar."
              : regrasDoItem
              ? "O Gemini propõe instruções para a IA identificar no vídeo e no áudio se este item foi cumprido. Confira as regras e marque as que deseja adotar."
              : soGemini
              ? "O Gemini analisa o rascunho sob o prisma pedagógico e de detectabilidade multimodal. Marque o que quiser adotar — o que você não marcar não toca o Rascunho."
              : soMedgemma
              ? t("interview_enrich_intro")
              : "Comparativo transparente entre a inteligência geral do Gemini e o parecer médico-acadêmico especializado do MedGemma 27B. Marque o que quiser adotar."}
          </Typography>
        </Box>
        <Divider />

        <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2 }}>
          {carregando && (
            <Box
              role="status"
              aria-live="polite"
              aria-busy
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
                py: 6,
              }}
            >
              <CircularProgress size={26} sx={{ color: "#04785d" }} />
              <Typography
                sx={{
                  fontSize: 14,
                  color: "#04785d",
                  textAlign: "center",
                }}
              >
                {enquadramentoFocal ? "O Gemini está preparando a recomendação de enquadramento." : regrasDoItem
                  ? "O Gemini está preparando as regras de análise deste item."
                  : t("interview_enrich_working_medical")}
              </Typography>

              {/* A referência ao processo: o que está sendo enviado AGORA. */}
              {resumoPayload && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                    justifyContent: "center",
                    mt: 0.5,
                  }}
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${resumoPayload.sections} ${t("interview_enrich_payload_sections")}`}
                    sx={{ fontSize: 11.5 }}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${resumoPayload.items} ${t("interview_enrich_payload_items")}`}
                    sx={{ fontSize: 11.5 }}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`~${resumoPayload.chars} ${t("interview_enrich_payload_chars")}`}
                    sx={{ fontSize: 11.5 }}
                  />
                </Box>
              )}
            </Box>
          )}
          {sincronizando && (
            <Box
              role="status"
              aria-live="polite"
              aria-busy
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
                py: 6,
              }}
            >
              <CircularProgress size={26} sx={{ color: "#04785d" }} />
              <Typography
                sx={{
                  fontSize: 13,
                  color: "#04785d",
                  textAlign: "center",
                  fontWeight: 600,
                }}
              >
                {regrasDoItem
                  ? "Sincronizando a resposta do Gemini com o servidor."
                  : t("interview_enrich_syncing_title")}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: palette.textMuted,
                  textAlign: "center",
                  maxWidth: 420,
                }}
              >
                {t("interview_enrich_syncing_desc")}
              </Typography>
            </Box>
          )}

          {!emEspera && erro && (!proposta || amendments.length === 0) && (
            <Box sx={{ py: 2 }}>
              <Typography sx={{ fontSize: 13, color: palette.dangerText, mb: 0.5, fontWeight: 600 }}>
                {erro}
              </Typography>
              <Typography sx={{ fontSize: 12, color: palette.textMuted }}>
                {enquadramentoFocal ? "Não foi possível concluir a revisão do enquadramento. Tente novamente." : regrasDoItem
                  ? "Não foi possível concluir a revisão das regras. Tente novamente."
                  : "A chamada ao MedGemma não pôde ser concluída. A GPU foi acionada — tente novamente."}
              </Typography>
            </Box>
          )}

          {!emEspera && !erro && amendments.length === 0 && blocos.length === 0 && (
            <Box sx={{ py: 2 }}>
              <Typography sx={{ fontSize: 14, color: palette.textMuted }}>
                {enquadramentoFocal ? proposta || solicitado
                  ? "O Gemini não propôs alterações para o enquadramento da câmera focal."
                  : "Peça ao Gemini uma recomendação de enquadramento para todo o checklist." : regrasDoItem
                  ? proposta || solicitado
                    ? "O Gemini não propôs alterações para as regras deste item."
                    : "Peça ao Gemini as regras para analisar este item no vídeo."
                  : proposta || solicitado
                  ? t("interview_enrich_nothing")
                  : t("interview_enrich_never_asked")}
              </Typography>
            </Box>
          )}

          {!emEspera && proposta && proposta.summary !== "" && (amendments.length > 0 || cameraFocal) && (
            <Typography
              sx={{
                fontSize: 13.5,
                lineHeight: 1.55,
                color: palette.textMuted,
                mb: 2,
              }}
            >
              {proposta.summary}
            </Typography>
          )}

          {!emEspera && nEstruturais > 1 && (
            <Typography sx={{ fontSize: 13.5, color: palette.warning, mb: 1.5 }}>
              {t("interview_enrich_structural_one_at_time")}
            </Typography>
          )}

          {!emEspera && temAlternativas && (
            <Typography sx={{ fontSize: 13.5, color: palette.warning, mb: 2 }}>
              {t("interview_enrich_alternatives_hint")}
            </Typography>
          )}

          {/* Duas colunas só em telas largas. Empilhado no resto: esconder um
              dos pareceres atrás de uma aba faria o professor decidir sem ler
              o outro, que é o oposto do que o comparativo existe para fazer. */}
          {!emEspera && (
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "1fr",
                  lg: blocos.length > 1 ? "1fr 1fr" : "1fr",
                },
                alignItems: "start",
              }}
            >
              {blocos.map(({ motor, grupos }) => (
                <ColunaDoMotor
                  key={motor.engine}
                  motor={motor}
                  grupos={grupos}
                  marcados={marcados}
                  adotando={adotando}
                  onAlternar={alternar}
                />
              ))}
            </Box>
          )}

          {/* Follow-up dentro do painel: uma ênfase + pedir de novo. Fica
              visível também com proposta na tela, para o professor refinar
              sem fechar a gaveta - fechar e reabrir não descarta nada. */}
          {!emEspera && (
            <Box
              component="section"
              aria-label={t("interview_enrich_followup_title")}
              sx={{
                mt: amendments.length > 0 ? 3 : 0,
                pt: amendments.length > 0 ? 2 : 0,
                borderTop: amendments.length > 0 ? `1px dashed ${palette.border}` : "none",
              }}
            >
              {amendments.length > 0 && (
                <Typography
                  sx={{ fontSize: 13, fontWeight: 700, color: palette.textMuted, mb: 1 }}
                >
                  {t("interview_enrich_followup_title")}
                </Typography>
              )}
              <TextField
                multiline
                minRows={2}
                fullWidth
                size="small"
                label={enquadramentoFocal ? "Ênfase para o enquadramento (opcional)" : regrasDoItem ? "Ênfase para as regras do item (opcional)" : t("interview_enrich_steer_label_medical")}
                placeholder={enquadramentoFocal ? "Indique o foco visual desejado e as condições de enquadramento relevantes para o cenário e o checklist." : cameraFocal ? "Indique o aspecto visual deste critério que precisa de mais detalhe e seu contexto no cenário." : regrasDoItem ? "Ex.: gesto visível, fala esperada ou critério para marcar não feito." : t("interview_enrich_steer_placeholder_medical") ?? ""}
                value={steer}
                onChange={(ev) => setSteer(ev.target.value)}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1, flexWrap: "wrap" }}>
                <Button
                  variant={amendments.length > 0 ? "outlined" : "contained"}
                  disabled={adotando || emEspera}
                  startIcon={regrasDoItem ? <BoltOutlinedIcon /> : <LocalHospitalOutlinedIcon />}
                  onClick={() => aoPedir(steer.trim())}
                  sx={
                    amendments.length > 0
                      ? {
                          color: regrasDoItem ? IDENTIDADE_MOTOR.gemini.texto : IDENTIDADE_MOTOR.medgemma.texto,
                          borderColor: regrasDoItem ? IDENTIDADE_MOTOR.gemini.texto : IDENTIDADE_MOTOR.medgemma.texto,
                        }
                      : {
                          backgroundColor: regrasDoItem ? IDENTIDADE_MOTOR.gemini.texto : IDENTIDADE_MOTOR.medgemma.texto,
                          "&:hover": { backgroundColor: regrasDoItem ? IDENTIDADE_MOTOR.gemini.cor : IDENTIDADE_MOTOR.medgemma.cor },
                        }
                  }
                >
                  {amendments.length > 0
                    ? t("interview_enrich_reprocess")
                    : enquadramentoFocal ? "Pedir enquadramento ao Gemini" : regrasDoItem
                    ? "Pedir regras ao Gemini"
                    : t("interview_enrich_ask_medical")}
                </Button>
                {amendments.length > 0 && (
                  <Typography sx={{ fontSize: 13, color: palette.textMuted }}>
                    {t("interview_enrich_reprocess_hint")}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {amendments.length > 0 && (
          <>
            <Divider />

            <Box
              sx={{
                px: 2.5,
                py: 2,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                pb: "calc(16px + env(safe-area-inset-bottom))",
              }}
            >
              <Button
                variant="contained"
                disabled={adotando || emEspera || escolhidos.length === 0}
                onClick={() => aoAdotar(escolhidos)}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                {escolhidos.length === 0
                  ? t("interview_enrich_adopt_none")
                  : `${t("interview_enrich_adopt")} (${escolhidos.length})`}
              </Button>

              <Button
                variant="text"
                disabled={adotando || emEspera}
                onClick={selecionarSemAlternativas}
              >
                {t("interview_enrich_select_safe")}
              </Button>

              {escolhidos.length > 0 && (
                <Typography sx={{ fontSize: 13, color: palette.textMuted }}>
                  {Object.entries(contagemPorMotor)
                    .map(([modelo, n]) => `${n} × ${modelo}`)
                    .join(" · ")}
                </Typography>
              )}

              <Box sx={{ flex: 1 }} />

              <Button
                variant="text"
                color="inherit"
                disabled={adotando || emEspera}
                onClick={aoDescartar}
              >
                {t("interview_enrich_discard")}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
}

/**
 * A região de um motor: cabeçalho com o nome do modelo, o parecer que ele
 * escreveu e as Emendas que ele propôs.
 *
 * O nome do modelo entra em `aria-label` porque é ele que identifica a coluna
 * para quem navega por leitor de tela — «a coluna verde» não existe ali.
 */
function ColunaDoMotor({
  motor,
  grupos,
  marcados,
  adotando,
  onAlternar,
}: {
  motor: InterviewEnrichmentEngine;
  grupos: { categoria: InterviewAmendment["category"]; itens: InterviewAmendment[] }[];
  marcados: Record<string, boolean>;
  adotando: boolean;
  onAlternar: (a: InterviewAmendment) => void;
}) {
  const { t } = useTranslation();
  const identidade = identidadeDe(motor.engine);
  const vazio = grupos.length === 0;
  const gruposCenario = grupos.filter((g) =>
    ["cena", "paciente", "ator", "ambiente"].includes(g.categoria)
  );
  const gruposChecklist = grupos.filter((g) => g.categoria === "checklist");
  const gruposLegados = grupos.filter((g) =>
    ["deteccao", "outros"].includes(g.categoria)
  );
  const renderGrupo = (g: typeof grupos[number]) => (
    <Box key={g.categoria} sx={{ mb: 2 }}>
      {(motor.engine !== "medgemma" || g.categoria !== "checklist") && (
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, letterSpacing: 0.6, fontSize: 10.5, color: palette.textMuted }}
        >
          {t(ROTULO_CATEGORIA[g.categoria]).toUpperCase()}
        </Typography>
      )}
      {g.itens.map((a) => (
        <CartaoEmenda
          key={a.id}
          emenda={a}
          marcado={Boolean(marcados[a.id])}
          disabled={adotando}
          onAlternar={() => onAlternar(a)}
        />
      ))}
    </Box>
  );
  const renderSecao = (
    titulo: string,
    lista: typeof grupos,
    cor: string,
    fundo: string
  ) => lista.length > 0 && (
    <Box
      component="section"
      role="group"
      aria-label={titulo}
      key={titulo}
      sx={{ mb: 2, border: `1px solid ${cor}66`, borderRadius: "10px", overflow: "hidden" }}
    >
      <Box sx={{ px: 1.5, py: 1.1, backgroundColor: fundo, borderBottom: `1px solid ${cor}66`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography component="h4" sx={{ fontWeight: 800, fontSize: 14, color: cor }}>
          {titulo}
        </Typography>
        <Chip size="small" label={lista.reduce((n, g) => n + g.itens.length, 0)} sx={{ height: 22, fontWeight: 700, color: cor, backgroundColor: palette.surface }} />
      </Box>
      <Box sx={{ px: 1.5, pt: 1.5 }}>{lista.map(renderGrupo)}</Box>
    </Box>
  );

  return (
    <Box
      component="section"
      role="region"
      aria-label={`${motor.model} — ${t(identidade.papel)}`}
      sx={{
        border: `1px solid ${identidade.cor}44`,
        borderTop: `3px solid ${identidade.cor}`,
        borderRadius: "12px",
        overflow: "hidden",
        background: motor.engine === "medgemma" ? "#fbfefc" : palette.surface,
        ...(motor.engine === "medgemma"
          ? { animation: `${surgirMedgemma} 360ms ease-out` }
          : {}),
      }}
    >
      <Box sx={{ px: 1.5, py: 1.25, background: identidade.fundo }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box sx={{ color: identidade.cor, display: "flex" }}>
            {iconeDe(motor.engine)}
          </Box>
          {/* O nome do motor é o modelo que respondeu, não uma constante: ver
              o comentário de `IDENTIDADE_MOTOR`. */}
          <Typography
            component="h3"
            sx={{ fontWeight: 700, fontSize: 14.5, color: identidade.texto }}
          >
            {motor.model}
          </Typography>
        </Box>
        <Typography sx={{ mt: 0.5, fontSize: 11.5, color: palette.textMuted }}>
          {t(identidade.papel)}
        </Typography>
        {motor.summary !== "" && (
          <Typography sx={{ mt: 0.75, fontSize: 13.5, color: palette.text }}>
            {motor.summary}
          </Typography>
        )}
      </Box>

      <Box sx={{ px: 1.5, py: 1.5 }}>
        {/* Motor fora do ar não apaga o parecer do outro: o comparativo vira
            parcial e diz isso, em vez de fingir que a rodada não aconteceu. */}
        {motor.status === "unavailable" && (
          <Alert severity="warning" sx={{ mb: vazio ? 0 : 1.5, fontSize: 13.5 }}>
            {motor.message ?? t("interview_enrich_engine_unavailable")}
          </Alert>
        )}

        {motor.status === "disabled" && (
          <Alert severity="info" sx={{ mb: vazio ? 0 : 1.5, fontSize: 13.5 }}>
            {motor.message ?? t("interview_enrich_engine_disabled")}
          </Alert>
        )}

        {/* O RASTRO da chamada: o professor vê o processo, não só o parecer.
            O payload enviado é o Rascunho que ele edita à esquerda; aqui vai o
            tamanho dele, a latência e a resposta bruta do modelo. */}
        {motor.trace && (
          <Box
            sx={{
              mb: vazio ? 0 : 1.5,
              p: 1.25,
              borderRadius: "8px",
              border: `1px solid ${palette.border}`,
              display: "grid",
              gap: 0.75,
            }}
          >
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
              <Chip
                size="small"
                variant="outlined"
                label={`${motor.trace.elapsed_s}s`}
                sx={{ fontSize: 11.5, fontWeight: 600 }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`${motor.trace.sections} ${t("interview_enrich_payload_sections")} · ${motor.trace.items} ${t("interview_enrich_payload_items")}`}
                sx={{ fontSize: 11.5 }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`~${motor.trace.payload_chars} ${t("interview_enrich_payload_chars")}`}
                sx={{ fontSize: 11.5 }}
              />
            </Box>
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                backgroundColor: "transparent",
                "&::before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreOutlinedIcon fontSize="small" />}
                sx={{ minHeight: 0, "& .MuiAccordionSummary-content": { my: 0 } }}
              >
                <Typography sx={{ fontSize: 11.5, color: palette.textMuted }}>
                  {t("interview_enrich_trace_raw")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1,
                    maxHeight: 200,
                    overflow: "auto",
                    borderRadius: "6px",
                    backgroundColor: "#1f2937",
                    color: "#f3f4f6",
                    fontSize: 11,
                    lineHeight: 1.45,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {rawLegivel(motor.trace.raw_response)}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}

        {vazio && motor.status === "completed" && (
          <Typography sx={{ fontSize: 13.5, color: palette.textMuted }}>
            {t("interview_enrich_engine_nothing")}
          </Typography>
        )}

        {motor.engine === "medgemma" ? (
          <>
            {renderSecao("Cenário", gruposCenario, palette.info, palette.infoSoft)}
            {renderSecao("Checklist", gruposChecklist, identidade.texto, identidade.fundo)}
            {gruposLegados.map(renderGrupo)}
          </>
        ) : grupos.map(renderGrupo)}
      </Box>
    </Box>
  );
}

/**
 * Um cartão de Emenda. O antes rotulado «Atual», o depois rotulado «Sugestão»,
 * e a razão clínica embaixo.
 *
 * Emenda que CRIA Item ou seção ganha o selo «novo» e não tem antes para
 * mostrar. Emenda que substitui mostra o texto gravado acima.
 */
function CartaoEmenda({
  emenda,
  marcado,
  disabled,
  onAlternar,
}: {
  emenda: InterviewAmendment;
  marcado: boolean;
  disabled: boolean;
  onAlternar: () => void;
}) {
  const { t } = useTranslation();
  const identidade = identidadeDe(emenda.engine);
  const ehMedgemma = emenda.engine === "medgemma";

  const cria = emenda.ops.some(
    (o) => o.op === "add_item" || o.op === "add_skill"
  );
  const variasAlteracoes = emenda.ops.length > 1;
  const temAlternativa = (emenda.conflicts_with ?? []).length > 0;

  return (
    <Box
      sx={{
        mt: 1,
        border: `1px solid ${marcado ? identidade.cor : ehMedgemma ? palette.emeraldDeep : palette.border}`,
        borderLeft: `3px solid ${identidade.cor}`,
        borderRadius: "10px",
        background: marcado
          ? identidade.fundo
          : ehMedgemma
          ? "#eefcf4"
          : palette.surface,
        transition: "border-color 160ms ease, background 160ms ease",
        ...(ehMedgemma
          ? { animation: `${surgirMedgemma} 420ms ease-out, ${brilhoMedgemma} 1.4s ease-out 420ms` }
          : {}),
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          px: 1.25,
          py: 1.25,
        }}
      >
        <Checkbox
          size="small"
          checked={marcado}
          disabled={disabled}
          onChange={onAlternar}
          inputProps={{ "aria-labelledby": `emenda-${emenda.id}-titulo` }}
          sx={{ p: 0.25, mt: "-2px" }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexWrap: "wrap",
            }}
          >
            <Typography
              id={`emenda-${emenda.id}-titulo`}
              sx={{ fontSize: 14.5, fontWeight: 600, color: palette.text }}
            >
              {renderTextoComNegrito(emenda.ops.some((op) => op.op === "set_detection_camera2" || op.op === "set_steps_camera2")
                ? emenda.label.replace(/câmera 2/gi, "Câmera focal") : emenda.label)}
            </Typography>

            {/* O modelo NÃO se repete por cartão. Numa coluna de dez Emendas
                isso imprimia o mesmo nome dez vezes e empurrava para baixo o
                texto que decide a adoção. A autoria vive no cabeçalho da
                coluna, que já é a região acessível daquele motor, e a borda
                colorida à esquerda amarra o cartão a ela. */}
            {cria && (
              <Chip
                size="small"
                label={t("interview_enrich_new")}
                sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
              />
            )}

            {temAlternativa && (
              <Chip
                size="small"
                icon={<WarningAmberOutlinedIcon sx={{ fontSize: 12 }} />}
                label={t("interview_enrich_alternative")}
                sx={{
                  height: 18,
                  fontSize: 10,
                  fontWeight: 700,
                  color: palette.warning,
                  background: palette.warningSoft,
                }}
              />
            )}
          </Box>

          {emenda.before !== null && (
            <Box sx={{ mt: 0.75 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, fontSize: 10, color: palette.textMuted }}
              >
                {t("interview_enrich_before_label").toUpperCase()}
              </Typography>
              <Typography
                sx={{
                  fontSize: variasAlteracoes ? 13.5 : 14,
                  lineHeight: 1.5,
                  color: palette.textMuted,
                }}
              >
                {emenda.before}
              </Typography>
            </Box>
          )}

          {!variasAlteracoes && (
            <Box sx={{ mt: 0.75 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, fontSize: 11, color: identidade.texto }}
              >
                {t("interview_enrich_after_label").toUpperCase()}
              </Typography>
              <Typography
                sx={{
                  fontSize: 14.5,
                  lineHeight: 1.6,
                  color: ehMedgemma ? "#04785d" : palette.text,
                  fontWeight: ehMedgemma ? 600 : 400,
                }}
              >
                {renderTextoComNegrito(emenda.after)}
              </Typography>
            </Box>
          )}

          {variasAlteracoes && (
            <Box
              sx={{
                mt: 1,
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: identidade.fundo,
                border: `1px solid ${identidade.cor}55`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 0.5,
                  fontWeight: 700,
                  color: identidade.texto,
                }}
              >
                {t("interview_enrich_after_label").toUpperCase()}
              </Typography>
              {emenda.ops.map((op, indice) => (
                <Typography
                  key={`${op.op}-${indice}`}
                  component="div"
                  sx={{
                    pl: 1,
                    fontSize: 14,
                    lineHeight: 1.55,
                    color: palette.text,
                    overflowWrap: "anywhere",
                    "&::before": {
                      content: '"•"',
                      mr: 0.75,
                      color: palette.textMuted,
                    },
                  }}
                >
                  {descreverOp(op)}
                </Typography>
              ))}
            </Box>
          )}

          {emenda.why !== "" && (
            <Typography
              sx={{
                mt: 0.75,
                fontSize: 13.5,
                lineHeight: 1.55,
                color: ehMedgemma ? "#04785d" : palette.textMuted,
                fontStyle: "italic",
              }}
            >
              {renderTextoComNegrito(emenda.why)}
            </Typography>
          )}

          {temFundamentacao(emenda.medical) && (
            <FundamentacaoClinica medical={emenda.medical!} />
          )}
        </Box>
      </Box>
    </Box>
  );
}

/**
 * A fundamentação clínica, sempre rotulada como NÃO VERIFICADA.
 *
 * Diretriz recordada por um modelo é pista para o professor conferir, não
 * fonte. Por isso nada aqui usa ícone ou palavra de validação — o rótulo do
 * accordion diz explicitamente que o conteúdo exige revisão humana.
 */
function FundamentacaoClinica({
  medical,
}: {
  medical: NonNullable<InterviewAmendment["medical"]>;
}) {
  const { t } = useTranslation();

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        mt: 1,
        border: `1px dashed ${palette.border}`,
        borderRadius: "8px",
        "&::before": { display: "none" },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreOutlinedIcon fontSize="small" />}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: palette.warning }}>
          {t("interview_enrich_medical_unverified")}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {medical.clinical_rationale !== "" && (
          <Typography sx={{ fontSize: 13.5, lineHeight: 1.6, color: palette.text }}>
            {medical.clinical_rationale}
          </Typography>
        )}

        {medical.pathophysiology !== "" && (
          <Typography
            sx={{ mt: 0.75, fontSize: 13.5, lineHeight: 1.6, color: palette.text }}
          >
            {medical.pathophysiology}
          </Typography>
        )}

        {medical.guidelines.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {medical.guidelines.map((g, i) => (
              <Typography
                key={`${g.organization}-${i}`}
                sx={{ fontSize: 13, lineHeight: 1.55, color: palette.textMuted }}
              >
                {[g.organization, g.reference, g.year, g.recommendation]
                  .filter((parte) => parte !== "" && parte !== null)
                  .join(" · ")}
              </Typography>
            ))}
          </Box>
        )}

        {medical.ontologies.length > 0 && (
          <Box sx={{ mt: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {medical.ontologies.map((o, i) => (
              <Chip
                key={`${o.system}-${o.code}-${i}`}
                size="small"
                label={`${o.system}: ${o.code} ${o.term}`.trim()}
                sx={{ height: 18, fontSize: 10 }}
              />
            ))}
          </Box>
        )}

        {medical.safety_flags.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {medical.safety_flags.map((flag, i) => (
              <Typography
                key={i}
                sx={{ fontSize: 13, lineHeight: 1.55, color: palette.dangerText }}
              >
                {flag}
              </Typography>
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
